import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { Stack, usePathname, useRouter } from 'expo-router';
import {
  isOnboardingComplete,
  ONBOARDING_TRIAL_ACTIVATION_ROUTE,
} from '@perdiem/trpc/onboarding';
import { ErrorState, LoadingSpinner } from '../../components';
import { trpc } from '../../lib/trpc';

const IS_LOCAL_DEV_BYPASS = process.env['EXPO_PUBLIC_DEV_BYPASS'] === 'true';

/**
 * Onboarding flow layout.
 * Stored step 0 is an unstarted setup state; visible setup screens and URLs
 * are one-based. Subscription activation is a gate between About You and the
 * rest of setup rather than a numbered step, so existing onboarding progress
 * values remain stable. Completion is controlled by the shared onboarding
 * helper.
 */
export default function OnboardingLayout() {
  const router = useRouter();
  const pathname = usePathname();
  const householdQ = trpc.households.get.useQuery();
  const subscriptionQ = trpc.subscription.getStatus.useQuery();

  // The About You step is free to complete: answering a few personal questions
  // builds commitment before the ask. The trial gate slides in right after it —
  // before accounts get connected, so the card always lands before the first
  // Plaid-billable item and before any bank credentials are requested.
  const isStepRoute = pathname?.includes('/step-') ?? false;
  const isAboutYouRoute = pathname?.includes('step-1-about-you') ?? false;
  const routeRequiresSubscription = isStepRoute && !isAboutYouRoute;
  // The onboarding index normally sends inactive households directly to the
  // dedicated trial route. Keep this guard for manually entered/deep-linked
  // numbered routes so they cannot be used to bypass the activation screen.
  const shouldRedirectToTrialActivation = Boolean(
    !IS_LOCAL_DEV_BYPASS
    && !householdQ.isLoading
    && !householdQ.error
    && !subscriptionQ.isLoading
    && !subscriptionQ.error
    && householdQ.data
    && !householdQ.data.isDemo
    && !subscriptionQ.data?.isActive
    && routeRequiresSubscription,
  );

  useEffect(() => {
    if (householdQ.isLoading || householdQ.error || !householdQ.data) {
      return;
    }

    if (isOnboardingComplete(householdQ.data.onboardingStep)) {
      router.replace('/(app)');
    }
  }, [householdQ.data, householdQ.error, householdQ.isLoading, router]);

  useEffect(() => {
    if (!shouldRedirectToTrialActivation) return;
    router.replace(ONBOARDING_TRIAL_ACTIVATION_ROUTE);
  }, [router, shouldRedirectToTrialActivation]);

  if (householdQ.isLoading || subscriptionQ.isLoading) {
    return <LoadingSpinner message="Checking your progress..." />;
  }

  if (householdQ.error || subscriptionQ.error) {
    return (
      <ErrorState
        message="Could not load your onboarding access."
        error={householdQ.error ?? subscriptionQ.error}
        screen="onboarding-layout"
        metadata={{
          failedQueries: householdQ.error ? 'households.get' : 'subscription.getStatus',
        }}
        onRetry={() => {
          void Promise.all([householdQ.refetch(), subscriptionQ.refetch()]);
        }}
      />
    );
  }

  if (!householdQ.data) {
    return (
      <ErrorState
        message="We couldn't find your onboarding progress."
        screen="onboarding-layout"
        metadata={{ failedQueries: 'households.get', householdMissing: true }}
        onRetry={() => {
          void householdQ.refetch();
        }}
      />
    );
  }

  if (isOnboardingComplete(householdQ.data.onboardingStep)) {
    return <LoadingSpinner message="Taking you back to your workspace..." />;
  }

  return (
    <View className="flex-1">
      <Stack screenOptions={{ headerShown: false, animation: 'slide_from_right' }}>
        <Stack.Screen name="step-1-about-you" options={{ title: 'Per Diem · Step 1: About You' }} />
        <Stack.Screen name="step-2-connect" options={{ title: 'Per Diem · Step 2: Connect Accounts' }} />
        <Stack.Screen name="step-3-transactions" options={{ title: 'Per Diem · Step 3: Verify Transactions' }} />
        <Stack.Screen name="step-4-income" options={{ title: 'Per Diem · Step 4: Verify Income' }} />
        <Stack.Screen name="step-5-budget" options={{ title: 'Per Diem · Step 5: Budget Review' }} />
        <Stack.Screen name="step-6-family" options={{ title: 'Per Diem · Step 6: Household' }} />
        <Stack.Screen name="step-7-life-events" options={{ title: 'Per Diem · Step 7: Life Events & FIRE Assumptions' }} />
        <Stack.Screen name="step-8-calculating" options={{ title: 'Per Diem · Step 8: Calculating' }} />
        <Stack.Screen name="trial-activation" options={{ title: 'Start Your Free Trial – Per Diem' }} />
      </Stack>
      {shouldRedirectToTrialActivation ? (
        <View style={StyleSheet.absoluteFill}>
          <LoadingSpinner message="Checking your trial access..." />
        </View>
      ) : null}
    </View>
  );
}
